/* eslint-disable no-param-reassign */
/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import { set } from 'lodash-es';
import {
  findCodeNafsByTerm,
} from '@/services/api/activity/codeNaf';
import { getCompanyFromApiSireneId as _getCompanyFromApiSireneId, createCompany } from '@/services/api/customer/company';
import { searchByCompanyName, searchBySiret } from '@/services/api/sirene/sirene';
import { createMember } from '@/services/api/customer/account';

// initial state
const state = {
  apeList: [],
  allActivities: [],
  companies: [],
  term: '',
  company: {},
};

// check if variable is a number
const isNumeric = (number) => !Number.isNaN(+number);

const isSiret = (number) => {
  const clean = number.split(' ').join('').trim();
  return isNumeric(clean) && clean.length === 14;
};

// getters
const getters = {
  get: (state) => state.companies.slice(0, 4),
  noop: () => {},
  term: (state) => state.term,
  getApeList: (state) => state.apeList.slice(0, 4),
  getAllActivities: (state) => state.allActivities.slice(0, 4),
  company: (state) => state.company,
};

// actions
const actions = {
  noop() {},
  clear({ commit }) { commit('CLEAR_COMPANIES'); },
  resetTerm({ commit }) {
    commit('RESET_TERM');
  },
  setValueWithPath({ commit }, object) {
    commit('setValueWithPath', object);
  },
  clearCompany({ commit }) {
    commit('CLEAR_COMPANY');
  },
  setValueDeep({ commit }, company) {
    commit('SET_VALUE_DEEP', company);
  },
  /**
   ** Set term to be researched
   * then fill companies list calling search action
   * @param {String} term
   */
  setTerm({ commit, dispatch }, object) {
    commit('SET_TERM', object.value);
    return dispatch('search');
  },
  setCodeNaf({ commit }, codeNaf) {
    commit('setCodeNaf', codeNaf);
  },
  setMainActivity({ commit }, mainActivity) {
    commit('setMainActivity', mainActivity);
  },
  async search({ commit, state }) {
    try {
      let companies = [];
      if (state.term !== '' && state.term.length >= 3) {
        if (isSiret(state.term)) {
          const siret = state.term.split(' ').join('').trim();
          const { data } = await searchBySiret(siret);
          companies = [data];
        } else {
          const { data } = await searchByCompanyName(state.term);
          companies = data;
        }
      }
      commit('SET_COMPANIES', companies);
    } catch (err) {
      commit('CLEAR_COMPANIES');
    }
  },
  /**
   ** Get company information from a company (id mandatory)
   * @param {*} unset
   * @param {Object} company to retriece
   * @returns a company object
   */
  async getCompany(unset, rcompany) {
    if (rcompany.id) {
      return _getCompanyFromApiSireneId(rcompany.id);
    }
    return Promise.resolve({ data: rcompany });
  },
  /**
   ** Retrieve an ape list
   * set a list of ape in store
   * @param {String} term to be searched
   */
  async setApeList({ commit, dispatch }, term) {
    try {
      const { data } = await findCodeNafsByTerm(term);
      commit('SET_APE_LIST', data);
      return Promise.resolve();
    } catch (err) {
      if (err.response && err.response.status === 404) {
        commit('SET_APE_LIST', []);
        return Promise.resolve();
      }
      dispatch('reporting/sendError', { err, currentPage: 'Ape list' }, { root: true });
      return Promise.reject();
    }
  },
  /**
   * create new company
   */
  async createCompany({ state, commit }) {
    const company = await createCompany({ ...state.company });
    commit('setValue', { company: {} });

    return company;
  },
  /**
   * create new account
   */
  async addCompanyMember({ rootGetters }, comp) {
    const client = rootGetters['client/get'];
    const clientCredentials = rootGetters['client/clientCredentials'];
    const { clientId } = clientCredentials;
    Object.assign(comp, {
      contacts: this.state.company.contacts,
    });
    Object.assign(client, {
      id: clientId,
    });
    const member = {
      company: comp,
      user: client,
      role: {
        id: 7,
        name: 'ROLE_ACCOUNT_ADMIN',
      },
    };
    await createMember({ ...member });
  },
};

// mutations
const mutations = {
  CLEAR_COMPANIES(state) {
    Object.assign(state, { companies: [] });
  },
  SET_COMPANIES(state, companies) {
    Object.assign(state, { companies });
  },
  SET_APE_LIST(state, apeList) {
    Object.assign(state, { apeList });
  },
  SET_TERM(state, term) {
    Object.assign(state, { term });
  },
  RESET_TERM(state) {
    Object.assign(state, { term: '' });
  },
  CLEAR_COMPANY(state) {
    Object.assign(state, {
      company: {
        siret: null,
        name: null,
        address: {
          street: null,
          postalCode: null,
          city: null,
          country: null,
        },
        employeesBracket: {
          code: null,
          name: null,
        },
        mainActivity: {
          id: null,
          name: null,
          subcategory: {
            industry: {
              id: null,
              name: null,
            },
            id: null,
            name: null,
          },
        },

        inCreation: null,
        formeJuridique: null,
        companyType: null,
        reportedRevenue: null,
        registrationDate: null,

        fundRaised: null,
        onlineRevenue: null,
        hasFundraised: null,
        negativeRevenue: null,
        codeNaf: {
          code: null,
          id: null,
          name: null,
        },
      },
    });
  },
  SET_VALUE_DEEP(state, company) {
    const keys = Object.keys(company);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (key in state) {
        const deepKeys = Object.keys(company[key]);
        for (let j = 0; j < deepKeys.length; j += 1) {
          const deepKey = deepKeys[j];
          if (deepKey in state[key]) {
            Object.assign(state[key], { [deepKey]: company[key][deepKey] });
          }
        }
      }
    }
  },
  setValueWithPath(state, { value, path }) {
    set(state, path, value);
  },
  setCodeNaf(state, codeNaf) {
    Object.assign(state.company, { codeNaf: { ...codeNaf } });
  },
  setMainActivity(state, mainActivity) {
    Object.assign(state.company, { mainActivity: { ...mainActivity } });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
